/**
 * Emergent Google Auth launcher.
 *
 * Native (Capacitor Android) flow — Jun 2026 "server mailbox" design:
 *  1. The app generates a random `handoff_id`, embeds it in the
 *     redirect (`/auth/callback?native=1&h=<id>`), and opens the auth
 *     URL in Chrome Custom Tabs (via @capacitor/browser — Custom Tabs
 *     share Chrome's cookies so the Google account picker appears).
 *  2. After Google auth, the callback page (still in the Custom Tab)
 *     DEPOSITS the session_id to POST /api/auth/native-handoff keyed
 *     by handoff_id, then best-effort bounces back via mesp://.
 *  3. Meanwhile the app POLLS GET /api/auth/native-handoff/<id> —
 *     the moment the session_id lands, it navigates to
 *     /auth/callback#session_id=… and completes the exchange INSIDE
 *     the app WebView (cookies land in the app, not Chrome).
 *  This makes login deterministic even when the mesp:// deep-link
 *  data handoff fails (observed on production: zero exchange POSTs
 *  reached the backend during failed attempts).
 *
 * On the web it remains a plain same-tab redirect.
 */
import axios from 'axios';
import { isNative } from '../lib/mobileBridge';
import { crumb } from './nativeAuthDebug';

const API_URL = process.env.REACT_APP_BACKEND_URL;

let _pollTimer = null;
let _pollDeadline = 0;

function _stopHandoffPolling() {
  if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null; }
}

function _startHandoffPolling(handoffId) {
  _stopHandoffPolling();
  _pollDeadline = Date.now() + 4 * 60 * 1000; // 4-minute window
  _pollTimer = setInterval(async () => {
    if (Date.now() > _pollDeadline) { _stopHandoffPolling(); return; }
    try {
      const r = await axios.get(
        `${API_URL}/api/auth/native-handoff/${handoffId}`,
        { timeout: 8000 }
      );
      const sid = r?.data?.session_id;
      if (sid) {
        _stopHandoffPolling();
        crumb('app_session_received', handoffId);
        window.location.assign(
          `/auth/callback#session_id=${encodeURIComponent(sid)}`
        );
      }
    } catch (_) { /* transient poll errors are fine — keep polling */ }
  }, 2500);
}

function _newHandoffId() {
  try {
    return window.crypto.randomUUID().replace(/-/g, '');
  } catch (_) {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Kick off the Emergent-managed Google OAuth flow.
 *
 * @param {Object} [opts]
 * @param {string} [opts.redirectPath='/auth/callback']
 * @param {string|null} [opts.entryIntent=null]
 *        Optional entry marker persisted to sessionStorage so
 *        AuthCallback can route back to the correct portal
 *        (e.g. ``legacy_student``, ``register``, ``login``).
 */
export function startEmergentGoogleAuth({
  redirectPath = '/auth/callback',
  entryIntent = null,
} = {}) {
  if (entryIntent) {
    try { sessionStorage.setItem('auth_entry', entryIntent); } catch (_) { /* private mode */ }
  }
  let native = false;
  try { native = !!(isNative && isNative()); } catch (_) { /* stubbed globals */ }

  let redirectUrl = window.location.origin + redirectPath;
  let handoffId = null;
  if (native) {
    handoffId = _newHandoffId();
    // PATH-based handoff (Jun 2026): production evidence showed the
    // second query param (`&h=…`) was stripped during the auth
    // provider's redirect round-trip — the tab never deposited while
    // the app polled 75×. A path segment survives any query-string
    // mangling. The /auth/callback/native/:handoffId route implies
    // native=1.
    redirectUrl = `${window.location.origin}/auth/callback/native/${handoffId}`;
  }
  const authUrl =
    'https://auth.emergentagent.com/?redirect=' +
    encodeURIComponent(redirectUrl);

  if (native) {
    crumb('app_auth_start', handoffId);
    _startHandoffPolling(handoffId);
    // Preferred: Chrome Custom Tabs via the @capacitor/browser plugin.
    // Custom Tabs SHARE Chrome's cookies, so the user's Google account
    // picker appears — and it always escapes the WebView regardless of
    // the allowNavigation list (deterministic, unlike window.open).
    const Browser = window.Capacitor?.Plugins?.Browser;
    if (Browser?.open) {
      Browser.open({ url: authUrl }).catch(() => {
        window.open(authUrl, '_blank', 'noopener,noreferrer');
      });
      return;
    }
    // Fallback (older APKs without the Browser plugin).
    window.open(authUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  window.location.href = authUrl;
}
